import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SuperSettings() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [exportData, setExportData] = useState(null);

  const load = async () => {
    const [{ data: settings }, { data: backupHistory }, { data: companies }] = await Promise.all([
      api.get("/super/settings"),
      api.get("/super/backup/history"),
      api.get("/super/companies"),
    ]);
    setForm(settings);
    setHistory(backupHistory || []);
    setTenants(companies || []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/super/settings", form);
      toast.success("Saved");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  const sendBackup = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/super/backup/send", { company_id: selectedTenant === "all" ? null : selectedTenant });
      toast.success(`Backup sent (${data.sent || 0} successful, ${data.failed || 0} failed)`);
      await load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setSending(false); }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.get(`/super/backup/export/${selectedTenant === "all" ? tenants[0]?.id || "" : selectedTenant}`);
      setExportData(data);
      const files = [
        { name: "clients.csv", content: data.clients_csv },
        { name: "bookings.csv", content: data.bookings_csv },
        { name: "invoices.csv", content: data.invoices_csv },
      ];
      files.forEach(({ name, content }) => {
        const blob = new Blob([content], { type: name.endsWith(".json") ? "application/json;charset=utf-8;" : "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
      toast.success("Backup CSV files downloaded");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="super-settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">System</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Platform settings</h1>
      </div>

      <Card className="bg-[#18181B] border-white/5 text-zinc-200">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">General</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Platform name</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.platform_name || ""} onChange={(e) => setForm({ ...form, platform_name: e.target.value })} data-testid="super-settings-name-input" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Support email</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} /></div>
            <div><Label>Support phone</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.support_phone || ""} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} /></div>
          </div>
          <div><Label>Terms of service</Label><Textarea className="bg-[#0a0a0c] border-white/10 mt-1.5" rows={5} value={form.terms_of_service || ""} onChange={(e) => setForm({ ...form, terms_of_service: e.target.value })} /></div>
          <div><Label>Privacy policy</Label><Textarea className="bg-[#0a0a0c] border-white/10 mt-1.5" rows={5} value={form.privacy_policy || ""} onChange={(e) => setForm({ ...form, privacy_policy: e.target.value })} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>WhatsApp phone number ID</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.whatsapp_phone_number_id || ""} onChange={(e) => setForm({ ...form, whatsapp_phone_number_id: e.target.value })} /></div>
            <div><Label>WhatsApp access token</Label><Input type="password" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.whatsapp_access_token || ""} onChange={(e) => setForm({ ...form, whatsapp_access_token: e.target.value })} /></div>
            <div><Label>WhatsApp template name</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.whatsapp_template_name || ""} onChange={(e) => setForm({ ...form, whatsapp_template_name: e.target.value })} /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#18181B] border-white/5 text-zinc-200">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Email backup</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2"><Checkbox checked={!!form.backup_enabled} onCheckedChange={(v) => setForm({ ...form, backup_enabled: !!v })} /><Label>Enable scheduled backups</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>SMTP host</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.smtp_host || ""} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} /></div>
            <div><Label>SMTP port</Label><Input type="number" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.smtp_port || ""} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} /></div>
            <div><Label>SMTP username</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.smtp_username || ""} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} /></div>
            <div><Label>SMTP password</Label><Input type="password" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.smtp_password || ""} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} /></div>
            <div><Label>From email</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.smtp_from_email || ""} onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })} /></div>
            <div><Label>Backup interval (days)</Label><Input type="number" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.backup_interval_days || ""} onChange={(e) => setForm({ ...form, backup_interval_days: Number(e.target.value) })} /></div>
          </div>
          <div className="flex items-center gap-2"><Checkbox checked={!!form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: !!v })} /><Label>Use TLS</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Tenant</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="bg-[#0a0a0c] border-white/10 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tenants</SelectItem>
                  {tenants.map((tenant) => <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={() => sendBackup()} disabled={sending} className="border-white/10 text-zinc-200">{sending ? "Sending…" : "Send backup now"}</Button>
              <Button variant="outline" onClick={downloadBackup} className="border-white/10 text-zinc-200">Download backup</Button>
            </div>
          </div>
          <div className="text-xs text-zinc-500">Attachments: clients.csv, bookings.csv, invoices.csv</div>
          {history.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-zinc-300">Recent history</div>
              {history.slice(0, 12).map((item, idx) => (
                <div key={idx} className="rounded border border-white/10 p-3 text-sm text-zinc-400">
                  <div className="flex items-center justify-between gap-2"><span>{item.company_name || item.company_id}</span><span className="capitalize">{item.status}</span></div>
                  <div className="text-xs mt-1">{item.sent_at ? new Date(item.sent_at).toLocaleString() : "—"}</div>
                  {item.error && <div className="text-xs text-red-400 mt-1">{item.error}</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#18181B] border-white/5 text-zinc-200">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Razorpay</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2"><Checkbox checked={!!form.razorpay_enabled} onCheckedChange={(v) => setForm({ ...form, razorpay_enabled: !!v })} /><Label>Enable Razorpay</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Mode</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.razorpay_mode || "sandbox"} onChange={(e) => setForm({ ...form, razorpay_mode: e.target.value })} /></div>
            <div><Label>Sandbox key ID</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.razorpay_key_id_sandbox || ""} onChange={(e) => setForm({ ...form, razorpay_key_id_sandbox: e.target.value })} /></div>
            <div><Label>Sandbox key secret</Label><Input type="password" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.razorpay_key_secret_sandbox || ""} onChange={(e) => setForm({ ...form, razorpay_key_secret_sandbox: e.target.value })} /></div>
            <div><Label>Live key ID</Label><Input className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.razorpay_key_id_live || ""} onChange={(e) => setForm({ ...form, razorpay_key_id_live: e.target.value })} /></div>
            <div><Label>Live key secret</Label><Input type="password" className="bg-[#0a0a0c] border-white/10 mt-1.5" value={form.razorpay_key_secret_live || ""} onChange={(e) => setForm({ ...form, razorpay_key_secret_live: e.target.value })} /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#18181B] border-white/5 text-zinc-200">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Webhooks</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="signup_webhook_url">Signup webhook URL</Label>
            <Input 
              id="signup_webhook_url"
              type="url"
              className="bg-[#0a0a0c] border-white/10 mt-1.5"
              value={form.signup_webhook_url || ""} 
              onChange={(e) => setForm({ ...form, signup_webhook_url: e.target.value })}
              placeholder="https://example.com/webhook/signup"
              data-testid="super-settings-webhook-url-input"
            />
            <div className="text-xs text-zinc-500 mt-2">
              This webhook will be called when a new user signs up with company details. It will receive a POST request with user and company information.
            </div>
          </div>
          <div className="p-4 bg-[#0a0a0c] rounded border border-white/10">
            <div className="text-sm font-medium text-zinc-300">Webhook payload example:</div>
            <pre className="mt-2 text-xs overflow-auto bg-[#000] border border-white/5 p-2 rounded max-h-40 text-zinc-400">
{`{
  "event": "new_signup",
  "company_id": "...",
  "company_name": "...",
  "user_name": "...",
  "email": "...",
  "phone": "...",
  "company_type": "...",
  "timestamp": "2024-..."
}`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full px-6" data-testid="super-settings-save-btn">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
